import React from 'react';

const transformOptions = (options, field) => {
    return options?.map((option) => ({
        label: option[field.optionLabel],
        value: option[field.optionValue],
    }));
};

const formatDate = (dateInput, isDateOnly = false) => {
    if (!dateInput) return "";
    try {
        const date = new Date(dateInput);
        const pad = (n) => n.toString().padStart(2, '0');
        const mm = pad(date.getMonth() + 1);
        const dd = pad(date.getDate());
        const yyyy = date.getFullYear();
        const base = `${mm}/${dd}/${yyyy}`;

        if (isDateOnly) return base;

        const hours = date.getHours();
        const minutes = pad(date.getMinutes());
        const ampm = hours >= 12 ? 'PM' : 'AM';
        const formattedHours = hours % 12 || 12;

        return `${base} ${formattedHours}:${minutes} ${ampm}`;
    } catch (error) {
        console.error("Invalid date format:", dateInput, error);
        return "";
    }
};

const RenderFields = ({
    fields,
    formData,
    handleChange,
    allOptions,
    readOnly = false,
    fieldTransformers = {},
    validationErrors = {},
}) => {
    return fields?.map((field) => {
        if (field.condition && !field.condition.values.includes(formData[field.condition.key])) {
            delete formData[field.key];
            return null;
        }

        const isFieldReadOnly = readOnly || field.key === 'contractId' || field.isDisabled;

        // Handle SELECT fields
        if (field.type === 'select' && (field.options || field.optionsKey)) {
            return (
                <RenderFieldsDropdown
                    key={field.key}
                    fields={[field]}
                    options={field?.options || transformOptions(allOptions[field.optionsKey], field)}
                    formData={formData}
                    handleChange={handleChange}
                    readOnly={readOnly}
                    validationErrors={validationErrors}
                />
            );
        }

        // Handle CHECKBOX fields
        if (field.type === 'checkbox') {
            return (
                <div key={field.key} className="flex items-center w-full pb-0">
                    <label className="w-1/3 h-10 flex items-center bg-gray-200 px-4 py-2 text-left font-bold text-sss-blue-sel border border-gray-300">
                        {field.label}
                    </label>
                    <input
                        name={field.key}
                        type="checkbox"
                        checked={formData[field.key]}
                        onChange={() =>
                            handleChange({
                                target: {
                                    name: field.key,
                                    value: !formData[field.key],
                                },
                            })
                        }
                        disabled={isFieldReadOnly}
                        className="w-5 h-5 rounded-none border-gray-300 ml-10"
                    />
                </div>
            );
        }

        // Handle DATE fields
        if (field.type === 'date' || field.type === 'datetime-local') {
            return (
                <div key={field.key} className="flex flex-col w-full">
                    <div className="flex items-center w-full pb-0">
                        <label className="w-1/3 h-10 flex items-center bg-gray-200 px-4 py-2 text-left font-bold text-sss-blue-sel border border-gray-300">
                            {field.label}
                        </label>

                        {isFieldReadOnly ? (

                            <span
                                className={`h-10 border ${validationErrors[field.key] ? 'border-red-500' : 'border-gray-300'} flex-grow px-4 bg-gray-100 flex items-center`}
                                style={{ minWidth: '200px' }}
                            >
                                {field.type === 'datetime-local' ? formatDate(formData[field.key], field.isDateOnly || false) : formatDate(formData[field.key])}
                            </span>

                        ) : (
                            <input
                                name={field.key}
                                type={field.type}
                                className={`h-10 border ${validationErrors[field.key] ? 'border-red-500' : 'border-gray-300'} flex-grow px-4 ${isFieldReadOnly ? 'bg-gray-100' : ''}`}
                                value={formData[field.key]}
                                onChange={(e) =>
                                    handleChange({ target: { name: field.key, value: e.target.value } })
                                }
                                readOnly={isFieldReadOnly}
                            />
                        )}

                    </div>
                    {validationErrors[field.key] && (
                        <div className="text-red-500 text-sm text-right">
                            {validationErrors[field.key]}
                        </div>
                    )}
                </div>
            );
        }

        // Generic field value display
        let value = '-';

        if (field.subKeys && Array.isArray(field.subKeys)) {
            const parent = formData[field.key] || {};
            value = field.subKeys.map((subKey) => parent[subKey]).filter(Boolean).join(" ") || '';
        } else if (field.options && Array.isArray(field.options)) {
            const match = field.options.find((opt) => opt.value === formData[field.key] || opt.key === formData[field.key]);
            value = match?.label || formData[field.key] || '';
        } else if (field.type === 'datetime-local') {
            value = formatDate(formData[field.key], field.isDateOnly);
        } else if (fieldTransformers[field.key]) {
            value = fieldTransformers[field.key](formData[field.key]);
        } else if (field.map) {
            value = field.map[formData[field.key]] || formData[field.key];
        } else {
            value = formData[field.key] ?? '';
        }

        return (
            <div key={field.key} className="flex flex-col w-full">
                <div className="flex items-center w-full pb-0">
                    <label className="w-1/3 h-10 flex items-center bg-gray-200 px-4 py-2 text-left font-bold text-sss-blue-sel border border-gray-300">
                        {field.label}
                    </label>
                    <input
                        name={field.key}
                        min={field.min || 0}
                        max={field.max || 100}
                        minLength={field.min || 0}
                        maxLength={field.max || 100}
                        type={field.type || 'text'}
                        className={`h-10 border ${validationErrors[field.key] ? 'border-red-500' : 'border-gray-300'} flex-grow px-4 ${isFieldReadOnly ? 'bg-gray-100' : ''}`}
                        value={value}
                        onChange={(e) =>
                            handleChange({
                                target: {
                                    name: field.key,
                                    value: field.type === 'number' ? Number(e.target.value) : e.target.value,
                                },
                            })
                        }
                        readOnly={isFieldReadOnly}
                    />
                </div>
                {validationErrors[field.key] && (
                    <div className="text-red-500 text-sm text-right">
                        {validationErrors[field.key]}
                    </div>
                )}
            </div>
        );
    });
};

const RenderFieldsDropdown = ({
    fields,
    options,
    formData,
    handleChange,
    readOnly = false,
    validationErrors = {},
}) => {
    return fields.map((field) => (
        <div key={field.key} className="flex flex-col w-full">
            <div className="flex justify-between items-center w-full pb-0">
                <label className="w-1/3 h-10 flex items-center bg-gray-200 px-4 py-2 text-left font-bold text-sss-blue-sel border border-gray-300">
                    {field.label}
                </label>
                <select
                    name={field.key}
                    className={`h-10 border ${validationErrors[field.key] ? 'border-red-500' : 'border-gray-300'} flex-grow px-4 my-auto ${readOnly ? 'bg-gray-100' : ''}`}
                    value={formData[field.key] || ''}
                    onChange={handleChange}
                    disabled={readOnly}
                >
                    <option value="">Select an option</option>
                    {options.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
            </div>
            {validationErrors[field.key] && (
                <div className="text-red-500 text-sm mt-1 ml-2">
                    {validationErrors[field.key]}
                </div>
            )}
        </div>
    ));
};



export { RenderFields, RenderFieldsDropdown };